import { useRef, useState, useEffect, ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  className?: string;
  /** Root margin for intersection observer - how early to start loading */
  rootMargin?: string;
  /** Minimum height placeholder to prevent layout shift */
  minHeight?: string;
  /** Fallback content while loading */
  fallback?: ReactNode;
}

/**
 * LazySection - Defers visual activation of below-the-fold content.
 * 
 * IMPORTANT FOR SEO: Children are ALWAYS rendered in the DOM so crawlers
 * can index the text content. Only visual effects (opacity/transform) are
 * deferred until the section enters the viewport.
 */
export function LazySection({
  children,
  className = '',
  rootMargin = '200px',
  minHeight = '400px',
  fallback
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if already in viewport on mount
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight + 200) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold: 0
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        minHeight: isVisible ? 'auto' : minHeight,
        contentVisibility: isVisible ? 'visible' : 'auto',
        containIntrinsicSize: isVisible ? 'auto' : minHeight,
      }}
    >
      {children}
    </div>
  );
}

export default LazySection;
